import * as React from 'react';
import { ActivityIndicator, Image, Text, View } from 'react-native';

import { getLocalizedTexts } from '../../Locales';
import color from '../../style/color';
import {
    UrlStep,
    InstallationStep,
    useLogEvents,
    useInstallationStep,
    useCloseObsoletePagesWhenSecondWaitingPageIsOpened,
    checkIsOsVersionMajorGreaterOrEqual,
} from '../../lib/mobileSafariExtension/mobileSafariExtension';
import {
    InstructionActivateJokoExtension,
    InstructionAllowAlways,
    InstructionAllowOnAllWebsites,
    InstructionClickBanner,
    InstructionClickExtensionWarningIcon,
    InstructionClickSafariSettings,
    InstructionManageExtensions,
    InstructionSystemSettingsAllow,
    InstructionSystemSettingsOtherWebsites,
    InstructionSystemSettingsClickButton,
} from '../../components/onboarding/mobileSafariExtension/Instructions';
import {
    containerStyleWithGap,
    getImageActivationPreambleStyle,
    newDesignSystemFontStyles,
    styles,
} from '../../components/onboarding/mobileSafariExtension/Styles';
import {
    NewDesignSystemButton,
    MissingVerifyBannerButton,
    TosConsentButton,
    onPressReturnToApp,
    onPressOpenSystemSettings,
    onPressStartActivation,
} from '../../components/onboarding/mobileSafariExtension/Buttons';
import { Header } from '../../components/onboarding/mobileSafariExtension/Header';
import { getRegion, localizeAsset, LocalizedAsset, Region } from '../../lib/auth/region';
import { InlineMp4 } from '../../components/onboarding/mobileSafariExtension/InlineMp4';
import {
    getDoesBelongToTestGroupPreRegistration,
    PreRegistrationTwoGroupAbTestSettingsKey,
} from '../../lib/mobileSafariExtension/preRegistrationUserSeed';

const widgetIllustrationActivationPreamble: LocalizedAsset = {
    [Region.FR]: { uri: '/assets/images/illustrations/mobile-safari-extension-installation/widget-offer.png' },
    [Region.US]: { uri: '/assets/images/illustrations/mobile-safari-extension-installation/widget-offer-us.png' },
    [Region.GB]: { uri: '/assets/images/illustrations/mobile-safari-extension-installation/widget-offer-us.png' },
};
const activationAnimation = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 1_activation BIG_longer_FR_aA.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 1_activation BIG_longer_US_aA.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 1_activation BIG_longer_US_aA.mp4',
};
const activationAnimationIos18 = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE_animations_flow_1_activation_BIG_longer_FR_1.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE_animations_flow_1_activation_BIG_longer_US_1.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE_animations_flow_1_activation_BIG_longer_US_1.mp4',
};
const activationAnimationPlaceholder =
    '/assets/images/illustrations/mobile-safari-extension-installation/activation-animation-placeholder.png';
const activationAnimationPlaceholderIos18 = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/activation-animation-placeholder-ios-18.png',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/activation-animation-placeholder-ios-18-us.png',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/activation-animation-placeholder-ios-18-us.png',
};
const authorizationWithBannerAnimation = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 2_authorisation with banner BIG_longer_FR.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 2_authorisation with banner BIG_longer_US.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 2_authorisation with banner BIG_longer_US.mp4',
};
const authorizationWithBannerAnimationPlaceholder = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-with-banner-animation-placeholder.png',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-with-banner-animation-placeholder-us.png',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-with-banner-animation-placeholder-us.png',
};
const authorizationFallbackAnimation = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_FR.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_US.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_US.mp4',
};
const authorizationFallbackAnimationIos18 = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_FR_EQUAL.mp4',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_US_EQUAL.mp4',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/MSE animations_flow 3_authorisation with aA BIG_longer_US_EQUAL.mp4',
};
const authorizationFallbackAnimationPlaceholder =
    '/assets/images/illustrations/mobile-safari-extension-installation/authorization-fallback-animation-placeholder.png';
const authorizationFallbackAnimationPlaceholderIos18 = {
    [Region.FR]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-fallback-animation-placeholder-ios-18.png',
    [Region.US]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-fallback-animation-placeholder-ios-18-us.png',
    [Region.GB]:
        '/assets/images/illustrations/mobile-safari-extension-installation/authorization-fallback-animation-placeholder-ios-18-us.png',
};
// Region-specific Amazon illustration for the conclusion (success) screen (FR: amazon.fr, US: amazon.com).
const successIllustration: LocalizedAsset = {
    [Region.FR]: {
        uri: '/assets/images/illustrations/mobile-safari-extension-installation/success-illustration-amazon-fr.png',
    },
    [Region.US]: {
        uri: '/assets/images/illustrations/mobile-safari-extension-installation/success-illustration-amazon-us.png',
    },
    [Region.GB]: {
        uri: '/assets/images/illustrations/mobile-safari-extension-installation/success-illustration-amazon-us.png',
    },
};
const tickCircleIcon = { uri: '/assets/images/icons/tick-circle.svg' };

export function MobileSafariExtensionInstallationPageContentRevamped({
    urlStep,
    isDevStackMode,
    userId,
    userSeed,
    deviceId,
    isOnboarding,
    isBeforeOnboarding,
}: {
    urlStep: UrlStep;
    isDevStackMode: boolean;
    userId: string | undefined;
    userSeed: number | undefined;
    deviceId: string | undefined;
    isOnboarding: boolean;
    isBeforeOnboarding: boolean;
}) {
    const shouldDisplayStepper = useShouldDisplayStepperInRevampedFlow(userSeed);
    const [step, setStep] = useInstallationStep({
        urlStep,
        userId,
        isDevStackMode,
        shouldSkipActivationPreamble: !isBeforeOnboarding,
        shouldSkipAuthorizationPreamble: true,
    });
    useLogEvents({
        urlStep,
        userId,
        deviceId,
        isDevStackMode,
        isOnboarding,
        isBeforeOnboarding,
        isFlowWithSystemSettings: false,
        isRevampedOnboardingFlow: true,
    });
    useCloseObsoletePagesWhenSecondWaitingPageIsOpened({
        step,
        userId,
        deviceId,
        isDevStackMode,
        isOnboarding,
        isBeforeOnboarding,
        isRevampedOnboardingFlow: true,
    });
    if (!step || step === InstallationStep.firstWaitingStep || step === InstallationStep.secondWaitingStep)
        return <WaitingScreen />;
    switch (step) {
        case InstallationStep.activateStepPreamble:
            return (
                <ActivationPreamble {...{ isDevStackMode, userId, deviceId, isOnboarding, isBeforeOnboarding, setStep }} />
            );
        case InstallationStep.activateStep:
            return <Activation {...{ shouldDisplayStepper }} />;
        case InstallationStep.authorizeWithBannerStep:
            return (
                <AuthorizationWithBanner {...{ step, setStep, userId, deviceId, isDevStackMode, shouldDisplayStepper }} />
            );
        case InstallationStep.authorizeStep:
            return <AuthorizationFallback {...{ shouldDisplayStepper }} />;
        case InstallationStep.missingAllWebsitesAuthorization:
            return <MissingAllWebsitesAuthorization {...{ shouldDisplayStepper }} />;
        case InstallationStep.missingAllWebsitesAuthorizationAfterRetry:
            return (
                <MissingAllWebsitesAuthorizationAfterRetry {...{ userId, deviceId, isDevStackMode, shouldDisplayStepper }} />
            );
        case InstallationStep.conclusionStep:
            return <Conclusion {...{ userId, deviceId }} />;
        default:
            throw new Error(`Invalid step: ${step}`);
    }
}

function WaitingScreen() {
    return (
        <View style={styles.containerWaitingScreen}>
            <ActivityIndicator size="large" color={color.alto} />
        </View>
    );
}

function ActivationPreamble({
    isDevStackMode,
    userId,
    deviceId,
    isOnboarding,
    isBeforeOnboarding,
    setStep,
}: {
    isDevStackMode: boolean;
    userId: string | undefined;
    deviceId: string | undefined;
    isOnboarding: boolean;
    isBeforeOnboarding: boolean;
    setStep: (step: InstallationStep) => void;
}) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    const [isTosConsentNeeded, setIsTosConsentNeeded] = React.useState<boolean>(isBeforeOnboarding);
    const region = getRegion();
    return (
        <View style={containerStyleWithGap(40)}>
            <Header shouldDisplayStepper={false} step={InstallationStep.activateStepPreamble} />
            <View style={styles.containerGreenCircleBackground}>
                <Image
                    source={localizeAsset(widgetIllustrationActivationPreamble)}
                    style={getImageActivationPreambleStyle(region)}
                />
            </View>
            <View style={styles.containerButtons}>
                <TosConsentButton
                    {...{
                        isBeforeOnboarding,
                        isTosConsentNeeded,
                        setIsTosConsentNeeded,
                        isDevStackMode,
                        userId,
                        deviceId,
                    }}
                />
                <View style={{ marginTop: 16 }}>
                    <NewDesignSystemButton
                        onPress={onPressStartActivation({
                            isDevStackMode,
                            userId,
                            deviceId,
                            isOnboarding,
                            isBeforeOnboarding,
                            setStep,
                        })}
                        disabled={isTosConsentNeeded}
                        textStyle={newDesignSystemFontStyles.actionMedium}>
                        {texts.activateStep.preamble.startButton}
                    </NewDesignSystemButton>
                </View>
            </View>
        </View>
    );
}

function Activation({ shouldDisplayStepper }: { shouldDisplayStepper: boolean | undefined }) {
    const region = getRegion();
    return (
        <View style={containerStyleWithGap(24)}>
            <Header shouldDisplayStepper={shouldDisplayStepper} step={InstallationStep.activateStep} />
            <View style={styles.containerInstructions}>
                <InstructionClickSafariSettings itemNumber={1} />
                <InstructionManageExtensions itemNumber={2} />
                <InstructionActivateJokoExtension itemNumber={3} />
            </View>
            {checkIsOsVersionMajorGreaterOrEqual(18) ? (
                <InlineMp4 src={activationAnimationIos18[region]} poster={activationAnimationPlaceholderIos18[region]} />
            ) : (
                <InlineMp4 src={activationAnimation[region]} poster={activationAnimationPlaceholder} />
            )}
        </View>
    );
}

function AuthorizationWithBanner({
    setStep,
    userId,
    deviceId,
    isDevStackMode,
    shouldDisplayStepper,
}: {
    setStep: (step: InstallationStep) => void;
    userId?: string;
    deviceId?: string;
    isDevStackMode?: boolean;
    shouldDisplayStepper: boolean | undefined;
}) {
    const region = getRegion();
    return (
        <>
            <View style={containerStyleWithGap(24)}>
                <Header shouldDisplayStepper={shouldDisplayStepper} step={InstallationStep.authorizeWithBannerStep} />
                <View style={styles.containerInstructionsWithMissingBannerButton}>
                    <View style={styles.containerInstructions}>
                        <InstructionClickBanner itemNumber={1} />
                        <InstructionAllowAlways itemNumber={2} />
                        <InstructionAllowOnAllWebsites itemNumber={3} />
                    </View>
                    <MissingVerifyBannerButton {...{ setStep, userId, deviceId, isDevStackMode }} />
                </View>
                <InlineMp4
                    src={authorizationWithBannerAnimation[region]}
                    poster={authorizationWithBannerAnimationPlaceholder[region]}
                />
            </View>
        </>
    );
}

function AuthorizationFallback({ shouldDisplayStepper }: { shouldDisplayStepper: boolean | undefined }) {
    const region = getRegion();
    return (
        <View style={containerStyleWithGap(24)}>
            <Header shouldDisplayStepper={shouldDisplayStepper} step={InstallationStep.authorizeStep} />
            <View style={styles.containerInstructions}>
                <InstructionClickSafariSettings itemNumber={1} />
                <InstructionClickExtensionWarningIcon itemNumber={2} />
                <InstructionAllowAlways itemNumber={3} />
                <InstructionAllowOnAllWebsites itemNumber={4} />
            </View>
            {checkIsOsVersionMajorGreaterOrEqual(18) ? (
                <InlineMp4
                    src={authorizationFallbackAnimationIos18[region]}
                    poster={authorizationFallbackAnimationPlaceholderIos18[region]}
                />
            ) : (
                <InlineMp4 src={authorizationFallbackAnimation[region]} poster={authorizationFallbackAnimationPlaceholder} />
            )}
        </View>
    );
}

function MissingAllWebsitesAuthorization({ shouldDisplayStepper }: { shouldDisplayStepper: boolean | undefined }) {
    const region = getRegion();
    return (
        <View style={containerStyleWithGap(24)}>
            <Header shouldDisplayStepper={shouldDisplayStepper} step={InstallationStep.missingAllWebsitesAuthorization} />
            <View style={styles.containerSplitInstructions}>
                <View style={styles.containerInstructions}>
                    <InstructionClickSafariSettings itemNumber={1} />
                    <InstructionClickExtensionWarningIcon itemNumber={2} />
                </View>
                <View style={[styles.containerInstructions, { backgroundColor: color.pippin }]}>
                    <InstructionAllowAlways itemNumber={3} />
                    <InstructionAllowOnAllWebsites itemNumber={4} />
                </View>
            </View>
            {checkIsOsVersionMajorGreaterOrEqual(18) ? (
                <InlineMp4
                    src={authorizationFallbackAnimationIos18[region]}
                    poster={authorizationFallbackAnimationPlaceholderIos18[region]}
                />
            ) : (
                <InlineMp4 src={authorizationFallbackAnimation[region]} poster={authorizationFallbackAnimationPlaceholder} />
            )}
        </View>
    );
}

function MissingAllWebsitesAuthorizationAfterRetry({
    userId,
    deviceId,
    isDevStackMode,
    shouldDisplayStepper,
}: {
    userId: string | undefined;
    deviceId: string | undefined;
    isDevStackMode: boolean;
    shouldDisplayStepper: boolean | undefined;
}) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    return (
        <View style={containerStyleWithGap(24)}>
            <Header
                shouldDisplayStepper={shouldDisplayStepper}
                step={InstallationStep.missingAllWebsitesAuthorizationAfterRetry}
            />
            <View style={styles.body}>
                <View style={styles.containerInstructions}>
                    <InstructionSystemSettingsClickButton itemNumber={1} />
                    <InstructionSystemSettingsOtherWebsites itemNumber={2} />
                    <InstructionSystemSettingsAllow itemNumber={3} />
                </View>
                <NewDesignSystemButton
                    onPress={onPressOpenSystemSettings({
                        userId,
                        deviceId,
                        isDevStackMode,
                        from: 'missingAllWebsitesAuthorizationAfterRetryPage',
                    })}
                    textStyle={newDesignSystemFontStyles.actionMedium}>
                    {texts.missingAuthorization.systemSettingsButton}
                </NewDesignSystemButton>
            </View>
        </View>
    );
}

function Conclusion({ userId, deviceId }: { userId: string | undefined; deviceId: string | undefined }) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped;
    return (
        <View style={containerStyleWithGap(40)}>
            <Header
                shouldDisplayStepper={false}
                step={InstallationStep.conclusionStep}
                titleStyle={newDesignSystemFontStyles.headlineMedium}
            />
            <View style={styles.conclusionContent}>
                <View style={styles.conclusionIllustrationContainer}>
                    <Image source={localizeAsset(successIllustration)} style={styles.conclusionIllustration} />
                </View>
                <NewDesignSystemButton
                    onPress={onPressReturnToApp({ userId, deviceId })}
                    textStyle={newDesignSystemFontStyles.actionMedium}>
                    {texts.conclusion.returnToJokoButton}
                </NewDesignSystemButton>
            </View>
        </View>
    );
}

export function Stepper({ currentStep }: { currentStep: number }) {
    const texts = getLocalizedTexts().mobileSafariExtensionInstallationPagesRevamped.header;
    return (
        <View style={styles.containerStepper}>
            <Step
                isCompleted={currentStep > 0}
                isCurrentStep={currentStep === 0}
                label={texts.left}
                isFirstStep={true}
                isLastStep={false}
            />
            <Step
                isCompleted={currentStep > 1}
                isCurrentStep={currentStep === 1}
                label={texts.right}
                isFirstStep={false}
                isLastStep={true}
            />
        </View>
    );
}

function Step({
    isCompleted,
    isCurrentStep,
    label,
    isFirstStep,
    isLastStep,
}: {
    isCompleted: boolean;
    isCurrentStep: boolean;
    label: string;
    isFirstStep: boolean;
    isLastStep: boolean;
}) {
    return (
        <View style={styles.containerStep}>
            <View style={[styles.containerStepTop, !isCompleted ? { marginTop: 4 } : undefined]}>
                {isFirstStep ? (
                    <View style={[styles.stepLine, { height: 0 }]} />
                ) : (
                    <View
                        style={[
                            styles.stepLine,
                            !isCurrentStep && !isCompleted ? { backgroundColor: color.alto } : undefined,
                        ]}
                    />
                )}
                {isCompleted ? (
                    <Image source={tickCircleIcon} style={styles.imageTickCircleIcon} />
                ) : isCurrentStep ? (
                    <View style={styles.currentStep} />
                ) : (
                    <View style={styles.nextStep} />
                )}
                {isLastStep ? (
                    <View style={[styles.stepLine, { height: 0 }]} />
                ) : (
                    <View style={[styles.stepLine, !isCompleted ? { backgroundColor: color.alto } : undefined]} />
                )}
            </View>
            <Text
                style={[
                    styles.textStepLabel,
                    isCurrentStep ? { color: color.black } : undefined,
                    isCompleted ? { marginTop: 4 } : undefined,
                ]}>
                {label}
            </Text>
        </View>
    );
}

export function useShouldDisplayStepperInRevampedFlow(userSeed: number | undefined) {
    const [shouldDisplayStepperInRevampedFlow, setShouldDisplayStepperInRevampedFlow] = React.useState<boolean | undefined>(
        undefined
    );
    React.useEffect(() => {
        async function updateShouldUseRevampedOnboardingFlow() {
            const shouldDisplayStepperInRevampedFlowResult = await getDoesBelongToTestGroupPreRegistration(
                userSeed!,
                PreRegistrationTwoGroupAbTestSettingsKey.shouldDisplayStepperInRevampedFlow
            );
            setShouldDisplayStepperInRevampedFlow(shouldDisplayStepperInRevampedFlowResult);
        }
        updateShouldUseRevampedOnboardingFlow();
    }, []);
    return shouldDisplayStepperInRevampedFlow;
}

export function useShouldShowImprovedWrongBrowserScreen(userSeed: number | undefined) {
    const [shouldShowImprovedWrongBrowserScreen, setShouldShowImprovedWrongBrowserScreen] = React.useState<
        boolean | undefined
    >(undefined);
    React.useEffect(() => {
        async function updateShouldShowImprovedWrongBrowserScreen() {
            const shouldShowImprovedWrongBrowserScreenResult = await getDoesBelongToTestGroupPreRegistration(
                userSeed!,
                PreRegistrationTwoGroupAbTestSettingsKey.shouldShowImprovedWrongBrowserScreen
            );
            setShouldShowImprovedWrongBrowserScreen(shouldShowImprovedWrongBrowserScreenResult);
        }
        updateShouldShowImprovedWrongBrowserScreen();
    }, [userSeed]);
    return shouldShowImprovedWrongBrowserScreen;
}
